import React from 'react';
import {
  Zap,
  Shield,
  Globe,
  Smartphone,
  Palette,
  Code,
  Users,
  Star,
  Heart,
  Lightbulb,
  Target,
  Rocket,
} from 'lucide-react';
import type { FeaturesComponent as FeaturesComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';
import { getFontFamily } from '../utils/fontLoader';
import { ButtonLink } from './shared/ButtonLink';

interface FeaturesComponentProps {
  component: FeaturesComponentType;
}

// Icon mapping for common feature icons
const iconMap = {
  'drag-drop': Code,
  responsive: Smartphone,
  design: Palette,
  fast: Zap,
  secure: Shield,
  global: Globe,
  users: Users,
  star: Star,
  heart: Heart,
  lightbulb: Lightbulb,
  target: Target,
  rocket: Rocket,
  // Add direct mappings for the dropdown values
  zap: Zap,
  shield: Shield,
  globe: Globe,
  smartphone: Smartphone,
  palette: Palette,
  code: Code,
} as const;

export const FeaturesComponent: React.FC<FeaturesComponentProps> = ({ component }) => {
  const { data: rawData } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Normalize data to handle missing defaults and type mismatches
  const data = {
    ...rawData,
    // Ensure layout has a default value
    layout: rawData.layout || 'grid',
    // Ensure columns is a string (Roary might send it as a number)
    columns: String(rawData.columns || '3') as '1' | '2' | '3' | '4',
  };

  // Color priority: customColors (user explicit) > theme/brand package > stored data defaults
  const backgroundColor =
    data.customColors?.background || themeStyles.features?.background || data.backgroundColor;
  const titleColor = data.customColors?.title || themeStyles.features?.titleColor;
  const textColor = data.customColors?.text || themeStyles.features?.textColor || data.textColor;
  const cardBackground = data.customColors?.cardBackground || themeStyles.features?.cardBg;
  const cardBorder = data.customColors?.cardBorder || themeStyles.features?.borderColor;
  const iconColor =
    data.customColors?.iconColor ||
    (data.customColors as any)?.icon ||
    themeStyles.features?.iconColor;

  const layoutClasses = {
    grid: 'grid',
    list: 'flex flex-col space-y-8',
    carousel: 'flex overflow-x-auto space-x-6 pb-4',
  };

  const columnClasses = {
    '1': 'grid-cols-1',
    '2': 'grid-cols-1 md:grid-cols-2',
    '3': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    '4': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  const getIcon = (iconName?: string) => {
    if (!iconName) return Star;
    return iconMap[iconName as keyof typeof iconMap] || Star;
  };

  const getImageSizeClasses = () => {
    const sizeMap = {
      small: 'w-16 h-16',
      medium: 'w-24 h-24',
      large: 'w-32 h-32',
    };
    return sizeMap[data.imageSize || 'medium'];
  };

  const getImageShapeClasses = () => {
    const shapeMap = {
      square: 'rounded-none',
      circle: 'rounded-full',
      rounded: 'rounded-lg',
    };
    return shapeMap[data.imageShape || 'rounded'];
  };

  // Sort features by order before rendering
  const sortedFeatures = [...(data.features || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.features?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.features?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    const buttons = (data as any).buttons;
    if (!buttons || buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        {buttons.map((button: any, index: number) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block text-center"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor }}>
      <div className="max-w-7xl mx-auto min-w-0 px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4 break-words"
            style={{ color: titleColor, ...headingFontStyle }}
            dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
          />
          {data.subtitle && (
            <p
              className="text-lg md:text-xl opacity-80 break-words"
              style={{ color: textColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
            />
          )}
        </div>

        {/* Features Grid/List */}
        <div
          className={`${layoutClasses[data.layout]} ${data.layout === 'grid' ? `${columnClasses[data.columns]} [&>*]:min-w-0` : ''} gap-8`}
        >
          {sortedFeatures.map((feature, index) => {
            const IconComponent = getIcon(feature.icon);
            const displayMode = data.displayMode || 'icon';
            const showIcon = displayMode === 'icon' || displayMode === 'both';
            const showImage = displayMode === 'image' || displayMode === 'both';
            const featureImage = (feature as any).cloudfrontUrl || (feature as any).imageUrl;

            return (
              <div
                key={index}
                className={`
                  ${data.layout === 'carousel' ? 'flex-shrink-0 w-80 max-w-[85vw]' : ''}
                  min-w-0 p-8 rounded-xl
                  shadow-sm hover:shadow-md transition-shadow duration-200
                  border
                `}
                style={{
                  backgroundColor: cardBackground,
                  borderColor: cardBorder,
                }}
              >
                {/* Icon or Image */}
                <div className="mb-6 flex justify-center">
                  {showImage && featureImage ? (
                    <div
                      className={`${getImageSizeClasses()} ${getImageShapeClasses()} relative overflow-hidden`}
                    >
                      <img
                        src={featureImage}
                        alt={(feature as any).imageAlt || feature.title}
                        className={
                          !hasCropApplied((feature as any).crop) ? 'w-full h-full object-cover' : ''
                        }
                        style={
                          hasCropApplied((feature as any).crop)
                            ? getCroppedImageStyles((feature as any).crop)
                            : undefined
                        }
                      />
                    </div>
                  ) : showIcon ? (
                    <div
                      className="flex items-center justify-center w-12 h-12 rounded-lg"
                      style={{
                        backgroundColor: iconColor ? `${iconColor}20` : undefined,
                      }}
                    >
                      <IconComponent className="w-6 h-6" style={{ color: iconColor }} />
                    </div>
                  ) : null}
                </div>

                {/* Content */}
                <div className="text-center">
                  <h3
                    className="text-xl font-semibold mb-3 break-words"
                    style={{ color: titleColor, ...headingFontStyle }}
                    dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(feature.title) }}
                  />
                  <p
                    className="leading-relaxed break-words"
                    style={{ color: textColor, ...bodyFontStyle }}
                    dangerouslySetInnerHTML={{
                      __html: renderTextWithLineBreaks(feature.description),
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>
    </section>
  );
};
